import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * MobileSelect - Dropdown that works on iOS Safari
 * 
 * This is a simplified version that exactly mirrors the working
 * Fix 4 from SafariDropdownTestV2.js
 */
const MobileSelect = ({
  className,
  options = [],
  value,
  onChange,
  placeholder = 'Select',
  disabled = false,
  required = false,
  name,
  id,
  label,
  error,
  'data-testid': dataTestId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const touchHandledRef = useRef(false);

  // Find selected option
  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption?.label || placeholder;

  // Outside click handler - EXACTLY like Fix 4
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    // NO capture: true - this was causing issues!
    document.addEventListener('touchstart', handleOutsideClick, { passive: true });
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('touchstart', handleOutsideClick);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  // Safari reflow hack - EXACTLY like Fix 4
  const forceReflow = () => {
    document.body.style.display = 'table';
    setTimeout(() => {
      document.body.style.display = 'block';
    }, 0);
  };

  // Toggle handler - EXACTLY like Fix 4
  const handleOpen = () => {
    if (disabled) return;
    forceReflow();
    setIsOpen(!isOpen);
  };

  // Touch handler - EXACTLY like Fix 4
  const handleTouchEnd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    touchHandledRef.current = true;
    handleOpen();
    setTimeout(() => { touchHandledRef.current = false; }, 300);
  };

  // Click handler - EXACTLY like Fix 4
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (touchHandledRef.current) return;
    handleOpen();
  };

  // Selection handler
  const handleSelect = (optionValue) => {
    if (onChange) {
      onChange({
        target: { name, value: optionValue },
      });
    }
    setIsOpen(false);
  };

  return (
    <div 
      ref={wrapperRef}
      className="dropdown-wrapper"
      style={{
        position: 'relative',
        zIndex: isOpen ? 9999 : 50,
        overflow: 'visible',
        WebkitTransform: 'translate3d(0,0,0)',
        transform: 'translate3d(0,0,0)',
      }}
    >
      {/* Trigger Button - styled like Fix 4 */}
      <button
        type="button"
        onClick={handleClick}
        onTouchEnd={handleTouchEnd}
        disabled={disabled}
        data-testid={dataTestId}
        className={cn(
          'w-full px-4 py-3 rounded-xl border flex items-center justify-between text-left',
          'bg-card text-foreground transition-all duration-200',
          !value && 'text-muted-foreground',
          disabled && 'opacity-50 cursor-not-allowed',
          error ? 'border-red-500' : 'border-border',
          isOpen && 'border-primary ring-2 ring-primary/20'
        )}
        style={{
          WebkitTapHighlightColor: 'transparent',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          touchAction: 'manipulation',
          minHeight: '44px',
        }}
      >
        <span className="truncate text-sm">{displayValue}</span>
        <ChevronDown className={cn(
          'w-4 h-4 text-muted-foreground flex-shrink-0 ml-2 transition-transform duration-200',
          isOpen && 'rotate-180'
        )} />
      </button>

      {/* Dropdown List - EXACTLY like Fix 4 */}
      {isOpen && (
        <div 
          className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-xl max-h-[280px] overflow-y-auto"
          style={{
            position: 'absolute',
            zIndex: 99999,
            overflow: 'visible',
            WebkitTransform: 'translate3d(0,0,0)',
            transform: 'translate3d(0,0,0)',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {options.length === 0 ? (
            <div className="px-4 py-3 text-center text-muted-foreground text-sm">
              No options
            </div>
          ) : (
            options.map((option, index) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                disabled={option.disabled}
                className={cn(
                  'w-full px-4 py-3 text-left flex items-center justify-between text-sm',
                  index === 0 && 'rounded-t-xl',
                  index === options.length - 1 && 'rounded-b-xl',
                  option.value === value 
                    ? 'bg-primary/10 text-primary font-medium' 
                    : 'text-foreground hover:bg-secondary active:bg-secondary',
                  option.disabled && 'opacity-50 cursor-not-allowed'
                )}
                style={{
                  WebkitTapHighlightColor: 'transparent',
                  minHeight: '44px',
                }}
              >
                <span>{option.label}</span>
                {option.value === value && (
                  <Check className="w-4 h-4 text-primary flex-shrink-0" />
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Export both named and default
export { MobileSelect };
export default MobileSelect;

// Also export utility functions for other components
export const isIOS = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

export const isSafari = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('safari') && !ua.includes('chrome') && !ua.includes('chromium');
};

export const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768 || 'ontouchstart' in window;
};
